import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { SectionHead } from "@/components/SectionHead";
import { useLang } from "@/LanguageContext";
import { GALLERY, GALLERY_FILTERS } from "@/data/site";

export const Gallery = () => {
  const { t, tr } = useLang();
  const [filter, setFilter] = useState("All");
  const [index, setIndex] = useState(null);

  const items = useMemo(
    () => (filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter)),
    [filter]
  );

  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, items.length]);

  const active = index !== null ? items[index] : null;

  return (
    <section id="work" data-testid="gallery-section" className="border-b border-steel-700 bg-steel-900 py-20 md:py-32">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8 lg:px-12">
        <SectionHead
          label={t.gallery.label}
          title={t.gallery.title}
          sub={t.gallery.sub}
          testid="gallery-head"
        />

        <div className="mb-8 flex flex-wrap gap-2" data-testid="gallery-filters">
          {GALLERY_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => {
                setFilter(f);
                setIndex(null);
              }}
              data-testid={`gallery-filter-${f.toLowerCase()}`}
              className={`rounded-sm border px-4 py-2 font-head text-sm font-bold uppercase tracking-[0.1em] transition-colors duration-200 ${
                filter === f
                  ? "border-forge bg-forge text-white"
                  : "border-steel-700 bg-transparent text-steel-400 hover:border-steel-400 hover:text-steel-50"
              }`}
            >
              {t.gallery.filters[f]}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {items.map((g, i) => (
              <motion.button
                key={g.id}
                layout
                type="button"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.28, delay: i * 0.03 }}
                onClick={() => setIndex(i)}
                data-testid={`gallery-item-${g.id}`}
                className="group relative aspect-[4/3] overflow-hidden rounded-sm border border-steel-700 text-left transition-colors duration-300 hover:border-forge"
              >
                <img
                  src={g.image}
                  alt={tr(g, "title")}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-steel-950 via-steel-950/20 to-transparent opacity-90" />
                <Expand
                  size={16}
                  className="absolute right-3 top-3 text-steel-50 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                />
                {g.real && (
                  <span className="absolute left-3 top-3 rounded-sm bg-forge px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-white">
                    {t.gallery.realBadge}
                  </span>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-forge-soft">
                    {t.gallery.filters[g.category]}
                  </p>
                  <p className="mt-1 font-head text-base font-bold uppercase leading-tight tracking-wide text-steel-50 sm:text-lg">
                    {tr(g, "title")}
                  </p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            aria-label={tr(active, "title")}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/92 p-4"
            onClick={() => setIndex(null)}
          >
            <button
              type="button"
              aria-label="Close"
              data-testid="lightbox-close"
              onClick={() => setIndex(null)}
              className="absolute right-4 top-4 rounded-sm border border-steel-700 p-2 text-steel-50 transition-colors duration-200 hover:border-forge hover:text-forge-light"
            >
              <X size={22} />
            </button>
            <button
              type="button"
              aria-label="Previous"
              data-testid="lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                setIndex((i) => (i - 1 + items.length) % items.length);
              }}
              className="absolute left-3 rounded-sm border border-steel-700 p-2 text-steel-50 transition-colors duration-200 hover:border-forge md:left-8"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              type="button"
              aria-label="Next"
              data-testid="lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                setIndex((i) => (i + 1) % items.length);
              }}
              className="absolute right-3 rounded-sm border border-steel-700 p-2 text-steel-50 transition-colors duration-200 hover:border-forge md:right-8"
            >
              <ChevronRight size={24} />
            </button>

            <motion.figure
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.22 }}
              className="max-h-[86vh] w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.image} alt={tr(active, "title")} className="max-h-[70vh] w-full object-contain" />
              <figcaption className="mt-4 border-t border-steel-700 pt-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-forge-soft">
                  {t.gallery.filters[active.category]}
                </p>
                <h3 className="mt-1 font-head text-2xl font-bold uppercase tracking-wide text-steel-50">
                  {tr(active, "title")}
                </h3>
                <p className="mt-1 text-sm text-steel-400">{tr(active, "caption")}</p>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
